/**
 * 地図を待つ間の代わり（#179、10/13 射場さん）。
 * 地図の部品（SiteMap・GoogleSiteMap）は lazy で、届くまでの代わりに高さが無いと、地図が出た瞬間に下の欄（社内メモ・見積のタブ）が
 * 350〜410px 下へずれ、ずれる前の位置を押して押し間違える（split-billing の揺れ、docs/16 の揺れの一覧）。
 * そこで 見出し・地図の箱・下のボタンの段 を地図と同じ形（同じ言葉の押せないボタン）で先に描き、高さをそろえる。
 *   - 下のボタンの段に何が出るかは siteMapActions 1か所（地図の部品も同じ関数を使う＝食い違わない）
 *   - 地図の種類（地理院）は端末に覚えた種類を押した形で（✓ の分の幅も同じ）
 *   - CSS は地図と同じ site-map.css（こちらは lazy にしない＝待つ間から効く）
 * 受け入れは e2e layout-shift.spec.ts ①
 */
import { SITE_MAP_STYLE_LABEL, SiteMapStyle, type GroupDetail } from "@genbaryoku/contract";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { t } from "../labels.js";
import { FOLD_MSG } from "./FoldBand.jsx";
import "./site-map.css";

export type SiteMapProvider = "google" | "gsi";

const STYLE_KEY = "gb.map.style";
/** 地図の種類（地理院）。端末ごとに覚える（localStorage gb.map.style） */
export function readMapStyle(): SiteMapStyle {
  try {
    const v = localStorage.getItem(STYLE_KEY);
    const p = SiteMapStyle.safeParse(v);
    return p.success ? p.data : "std";
  } catch {
    return "std";
  }
}
export function writeMapStyle(v: SiteMapStyle) {
  try {
    localStorage.setItem(STYLE_KEY, v);
  } catch {
    /* 端末が覚えられなくても動く */
  }
}

/**
 * 地図の下のボタンの段（ピンを直していないとき）に出るもの。地図の部品と待つ間の代わりが同じ答えを使う。
 *   regeocode：「住所から再取得」（手で直したピンがあるとき）／「住所から座標を取得」（地理院で、現場住所があって座標が無いとき）／null
 *   reverse：「ピン位置から住所入力」（手で直したピンがあって、ジオコーダがあるとき）
 */
export function siteMapActions(g: Pick<GroupDetail, "siteAddress" | "siteGeo">, provider: SiteMapProvider, canReverse: boolean): { regeocode: string | null; reverse: boolean } {
  const geo = g.siteGeo;
  const manual = geo?.source === "manual";
  if (provider === "google") return { regeocode: manual && g.siteAddress ? "住所から再取得" : null, reverse: manual && canReverse };
  return { regeocode: g.siteAddress && (!geo || manual) ? (geo ? "住所から再取得" : "住所から座標を取得") : null, reverse: manual && canReverse };
}

/**
 * マップの開閉（#235、docs/94 A-①、10/26 射場さん）。
 *   - 見出しの1行（48px）：ピンの絵・「マップ」・{siteAddress}（無ければ「…が未登録です」）・右に「開く ⌄」／「閉じる ⌃」
 *   - 住所も手で置いたピンも無い案件は畳んで始め、住所のある案件は開いて始める。押した「閉じる」はこの端末に覚える（gb.map.closed）
 *   - 畳んでいる間は地図の部品も iframe も読まない（呼ぶ側が開いたときだけ描く）
 */
const FOLD_KEY = "gb.map.closed";
function readMapClosed(): boolean {
  try {
    return localStorage.getItem(FOLD_KEY) === "1";
  } catch {
    return false;
  }
}
function writeMapClosed(v: boolean) {
  try {
    if (v) localStorage.setItem(FOLD_KEY, "1");
    else localStorage.removeItem(FOLD_KEY);
  } catch {
    /* 端末が覚えられなくても動く */
  }
}
export type SiteMapFold = { open: boolean; onToggle: () => void };
/** 住所か手で置いたピンがあれば開いて始める（押して閉じた端末は閉じたまま） */
function mapHasPlace(g: Pick<GroupDetail, "siteAddress" | "siteGeo">): boolean {
  return !!g.siteAddress || g.siteGeo?.source === "manual";
}
export function useSiteMapFold(g: Pick<GroupDetail, "siteAddress" | "siteGeo">): SiteMapFold {
  const [open, setOpen] = useState(() => mapHasPlace(g) && !readMapClosed());
  // 住所を入れて保存したらその場で開く（閉じた覚えも消す）
  const had = useRef(!!g.siteAddress);
  useEffect(() => {
    const has = !!g.siteAddress;
    if (has && !had.current) {
      writeMapClosed(false);
      setOpen(true);
    }
    had.current = has;
  }, [g.siteAddress]);
  return {
    open,
    onToggle: () =>
      setOpen((v) => {
        writeMapClosed(v);
        return !v;
      }),
  };
}

function PinGlyph() {
  return (
    <svg className="smpin" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 21s-6.5-5.6-6.5-11a6.5 6.5 0 0 1 13 0c0 5.4-6.5 11-6.5 11z" />
      <circle cx="12" cy="10" r="2.4" />
    </svg>
  );
}

/**
 * 見出しの1行。children（地図の種類・置き方の案内）は開いているときだけ。
 * #244（docs/94 B-5）：開閉の帯（FoldBand）と同じ見出しの形（.fold-h の絵・見出し・要約・「開く ⌄／閉じる ⌃」）
 */
export function SiteMapHead({ g, fold, big, children }: { g: Pick<GroupDetail, "siteAddress">; fold: SiteMapFold; big?: boolean; children?: ReactNode }) {
  return (
    <div className="fold-h smhead">
      <span className="fold-ic" aria-hidden="true">
        <PinGlyph />
      </span>
      <span className="fold-t smtitle">マップ</span>
      <span className={`fold-sum smaddr${g.siteAddress ? "" : " is-none"}`} data-testid="map-head-address" title={g.siteAddress || undefined}>
        {g.siteAddress || t("{siteAddress}が未登録です")}
      </span>
      {fold.open ? children : <span className="sp" />}
      {big ? null : (
        <button type="button" className="btn text sm fold-btn smfold" aria-expanded={fold.open} onClick={fold.onToggle} data-testid="map-fold">
          {fold.open ? FOLD_MSG.close : FOLD_MSG.open} <span aria-hidden="true">{fold.open ? "⌃" : "⌄"}</span>
        </button>
      )}
    </div>
  );
}

/** 畳んだマップ（見出しの1行だけ。地図の部品は読まない） */
export function SiteMapFolded({ g, fold }: { g: GroupDetail; fold: SiteMapFold }) {
  return (
    <section className="sitemap is-folded" aria-label="マップ" data-testid="site-map-folded">
      <SiteMapHead g={g} fold={fold} />
    </section>
  );
}

export function SiteMapLoading({ g, provider, canReverse, fold }: { g: GroupDetail; provider: SiteMapProvider; canReverse: boolean; fold: SiteMapFold }) {
  const styles: { key: string; label: string }[] =
    provider === "google"
      ? [
          { key: "roadmap", label: "標準" },
          { key: "satellite", label: "航空写真" },
        ]
      : (["std", "pale", "photo"] as const).map((k) => ({ key: k, label: SITE_MAP_STYLE_LABEL[k] }));
  const pressed = provider === "google" ? "roadmap" : readMapStyle();
  const a = siteMapActions(g, provider, canReverse);
  return (
    <section className="sitemap" aria-label="マップ" aria-busy="true" data-testid="site-map-loading" data-provider={provider === "google" ? "google" : undefined}>
      <SiteMapHead g={g} fold={fold}>
        <span className="sp" />
        <span className="seg fit" aria-hidden="true">
          {styles.map((s) => (
            <button key={s.key} type="button" disabled tabIndex={-1} aria-pressed={s.key === pressed}>
              {s.label}
            </button>
          ))}
        </span>
      </SiteMapHead>
      <div className="smbox">
        <div className="smmap smempty">読み込んでいます…</div>
      </div>
      <div className="smbtns" aria-hidden="true">
        {a.regeocode ? (
          <button type="button" className="btn" disabled tabIndex={-1}>
            {a.regeocode}
          </button>
        ) : null}
        {a.reverse ? (
          <button type="button" className="btn" disabled tabIndex={-1}>
            ピン位置から住所入力
          </button>
        ) : null}
        <span className="sp" />
      </div>
    </section>
  );
}
